<template>
    <div>
        <div class="card border-0 shadow-sm" style="border-radius: 12px;">
            <div class="card-header bg-white border-0 pt-4 px-4 pb-0">
                <h2 class="h5 font-weight-bold mb-0 text-dark">
                    <i class="far fa-box-open mr-2 text-primary"></i> Articles ({{ total }})
                </h2>
            </div>
            
            <div class="card-body p-4">
                <!-- Toolbar : recherche + filtre stock -->
                <div class="d-flex align-items-center mb-3" style="gap: 10px; flex-wrap: wrap;">
                    <div class="input-group flex-grow-1" style="min-width: 200px; max-width: 400px;">
                        <div class="input-group-prepend">
                            <span class="input-group-text">
                                <i class="far fa-search text-muted"></i>
                            </span>
                        </div>
                        <input type="text" 
                               class="form-control" 
                               v-model="searchQuery" 
                               @input="debounceSearch" 
                               placeholder="Rechercher par référence ou nom...">
                    </div>
                    <select v-model="stockFilter" @change="onFilterChange" class="form-control form-control-sm fam-filter-select">
                        <option value="all">Tous les articles</option>
                        <option value="in_stock">En stock uniquement</option>
                        <option value="out_of_stock">Rupture uniquement</option>
                    </select>
                </div>

                <!-- Toggle d'inclusion des sous-familles + per-page -->
                <div class="mb-3 d-flex align-items-center justify-content-between flex-wrap" style="gap: 10px;">
                    <div class="custom-control custom-switch">
                        <input type="checkbox" 
                               class="custom-control-input" 
                               id="includeChildrenToggle" 
                               v-model="includeChildren"
                               @change="fetchArticles">
                        <label class="custom-control-label font-weight-medium text-dark cursor-pointer" for="includeChildrenToggle" style="user-select: none;">
                            <i class="far fa-sitemap text-primary mr-1"></i> Inclure les sous-familles
                        </label>
                    </div>
                    <div class="d-flex align-items-center" style="gap: 8px;">
                        <span class="text-secondary small">Afficher</span>
                        <select v-model="perPage" @change="onPerPageChange" class="form-control form-control-sm fam-per-page-select">
                            <option :value="10">10</option>
                            <option :value="15">15</option>
                            <option :value="25">25</option>
                            <option :value="50">50</option>
                            <option :value="100">100</option>
                        </select>
                        <span class="text-secondary small">par page</span>
                    </div>
                </div>

                <!-- Indicateur de chargement -->
                <div v-if="loading && articles.length === 0" class="text-center py-5">
                    <i class="far fa-circle-notch fa-spin text-primary mr-2" style="font-size: 32px;"></i>
                    <p class="mt-2 text-muted small">Chargement des articles...</p>
                </div>

                <!-- Liste d'articles vide -->
                <div v-else-if="articles.length === 0" class="text-center py-5 text-secondary">
                    <i class="far fa-boxes text-muted mb-3" style="font-size: 48px;"></i>
                    <h3 class="h5 font-weight-bold text-dark mb-1">Aucun article</h3>
                    <p class="small mb-0" style="max-width: 300px; margin: 0 auto;">Cette famille ne contient aucun article correspondant à vos critères.</p>
                </div>

                <!-- Tableau des articles -->
                <div v-else class="ix-table-box table-responsive mb-0">
                    <table class="ix-table">
                        <thead>
                            <tr>
                                <th class="pl-3 fam-th-sortable" @click="toggleSort('name')">
                                    Article
                                    <i class="far ml-1" :class="sortBy === 'name' ? (sortDir === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary') : 'fa-sort text-muted'" style="font-size: 11px;"></i>
                                </th>
                                <th>Disponibilité</th>
                                <th class="text-center fam-th-sortable" @click="toggleSort('stock')">
                                    Stock
                                    <i class="far ml-1" :class="sortBy === 'stock' ? (sortDir === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary') : 'fa-sort text-muted'" style="font-size: 11px;"></i>
                                </th>
                                <th class="text-right fam-th-sortable" @click="toggleSort('selling_price')">
                                    Prix vente HT
                                    <i class="far ml-1" :class="sortBy === 'selling_price' ? (sortDir === 'asc' ? 'fa-sort-up text-primary' : 'fa-sort-down text-primary') : 'fa-sort text-muted'" style="font-size: 11px;"></i>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="art in articles" :key="art.id" style="border-bottom: 1px solid #f1f5f9;">
                                <td class="align-middle pl-3 py-2">
                                    <div class="d-flex align-items-center flex-wrap">
                                        <a :href="'/stocks/articles/' + art.id" class="ix-link font-weight-bold mr-2">{{ art.name }}</a>
                                        <span v-if="art.is_child_family && art.family_name" class="badge badge-primary-light" style="font-size: 10px; color: #3b82f6; background-color: #eff6ff; border: 1px solid #bfdbfe; border-radius: 4px; padding: 2px 6px;">
                                            <i class="far fa-folder mr-1"></i>{{ art.family_name }}
                                        </span>
                                    </div>
                                    <span v-if="art.number" class="text-secondary small font-mono">Ref: {{ art.number }}</span>
                                    <!-- SN collapsible -->
                                    <div v-if="art.has_serial_numbers && art.serial_numbers && art.serial_numbers.length > 0" class="mt-1">
                                        <button type="button" class="fam-sn-toggle" @click="toggleSnExpand(art.id)">
                                            <i class="far" :class="expandedSn.includes(art.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                                            <i class="far fa-barcode-read"></i>
                                            {{ art.serial_numbers.length }} numéro{{ art.serial_numbers.length > 1 ? 's' : '' }} de série
                                        </button>
                                        <div v-if="expandedSn.includes(art.id)" class="fam-sn-list">
                                            <div v-for="sn in art.serial_numbers" :key="sn" class="fam-sn-item">
                                                <i class="far fa-barcode-read mr-1"></i>{{ sn }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="align-middle py-2">
                                    <span class="ix-state" :class="art.state_color">{{ art.state_label }}</span>
                                </td>
                                <td class="text-center align-middle py-2">
                                    <div class="font-weight-bold" :class="art.available_stock > 0 ? 'text-dark' : 'text-danger'">
                                        {{ formatQuantity(art.available_stock) }} <span class="small text-muted font-weight-normal ml-1">{{ art.unit }}</span>
                                    </div>
                                </td>
                                <td class="text-right align-middle py-2">
                                    <span v-if="art.selling_price !== null">{{ formatPrice(art.selling_price) }}</span>
                                    <span v-else class="text-muted">-,-€</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination intelligente -->
                <div v-if="lastPage > 1" class="d-flex justify-content-between align-items-center mt-4 flex-wrap" style="gap: 10px;">
                    <span class="text-secondary small">
                        {{ (currentPage - 1) * perPage + 1 }}–{{ Math.min(currentPage * perPage, total) }} sur {{ total }}
                    </span>
                    <nav>
                        <ul class="pagination pagination-sm mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                                <button class="page-link" @click="changePage(currentPage - 1)"><i class="far fa-chevron-left"></i></button>
                            </li>
                            <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: p === currentPage, disabled: p === '...' }">
                                <button v-if="p !== '...'" class="page-link" @click="changePage(p)">{{ p }}</button>
                                <span v-else class="page-link">...</span>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === lastPage }">
                                <button class="page-link" @click="changePage(currentPage + 1)"><i class="far fa-chevron-right"></i></button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'article-family-articles-manager',
    props: {
        familyId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            articles: [],
            loading: true,
            searchQuery: '',
            debounceTimer: null,
            includeChildren: false,

            // Sorting
            sortBy: 'name',
            sortDir: 'asc',

            // Filtering
            stockFilter: 'all',

            // SN expansion
            expandedSn: [],

            // Pagination
            currentPage: 1,
            lastPage: 1,
            total: 0,
            perPage: 15
        };
    },
    computed: {
        visiblePages() {
            const pages = [];
            const current = this.currentPage;
            const last = this.lastPage;
            if (last <= 7) {
                for (let i = 1; i <= last; i++) pages.push(i);
                return pages;
            }
            pages.push(1);
            if (current > 3) pages.push('...');
            const start = Math.max(2, current - 1);
            const end = Math.min(last - 1, current + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (current < last - 2) pages.push('...');
            pages.push(last);
            return pages;
        }
    },
    methods: {
        formatQuantity(q) {
            if (q === null || q === undefined) return 0;
            return parseFloat(q);
        },

        formatPrice(p) {
            if (p === null || p === undefined) return '-,-€';
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p);
        },

        fetchArticles() {
            this.loading = true;
            const includeChildrenParam = this.includeChildren ? 1 : 0;
            const params = new URLSearchParams({
                page: this.currentPage,
                q: this.searchQuery,
                include_children: includeChildrenParam,
                sort_by: this.sortBy,
                sort_dir: this.sortDir,
                stock_filter: this.stockFilter,
                per_page: this.perPage
            });
            fetch(`/stocks/article-families/${this.familyId}/articles-list?${params}`, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            })
            .then(res => {
                if (!res.ok) throw new Error("Erreur de chargement.");
                return res.json();
            })
            .then(data => {
                if (data.success) {
                    this.articles = data.articles;
                    this.currentPage = data.current_page;
                    this.lastPage = data.last_page;
                    this.total = data.total;
                }
                this.loading = false;
            })
            .catch(err => {
                console.error(err);
                this.loading = false;
            });
        },

        toggleSort(column) {
            if (this.sortBy === column) {
                this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
            } else {
                this.sortBy = column;
                this.sortDir = 'asc';
            }
            this.currentPage = 1;
            this.fetchArticles();
        },

        onFilterChange() {
            this.currentPage = 1;
            this.fetchArticles();
        },

        onPerPageChange() {
            this.currentPage = 1;
            this.fetchArticles();
        },

        toggleSnExpand(articleId) {
            const idx = this.expandedSn.indexOf(articleId);
            if (idx > -1) {
                this.expandedSn.splice(idx, 1);
            } else {
                this.expandedSn.push(articleId);
            }
        },

        debounceSearch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.currentPage = 1;
                this.fetchArticles();
            }, 300);
        },

        changePage(p) {
            if (p < 1 || p > this.lastPage) return;
            this.currentPage = p;
            this.fetchArticles();
        }
    },
    mounted() {
        this.fetchArticles();
    }
};
</script>

<style scoped>
.input-group {
    border: 1px solid #cbd5e1;
    border-radius: 8px !important;
    background-color: #ffffff;
    transition: all 0.15s ease-in-out;
    overflow: hidden;
}

.input-group .form-control {
    border: none !important;
    height: 38px !important;
    box-shadow: none !important;
    background-color: transparent !important;
    font-size: 13.5px;
    color: #1e293b;
}

.input-group .input-group-text {
    border: none !important;
    background-color: transparent !important;
    color: #64748b;
    font-size: 14px;
    display: flex;
    align-items: center;
    padding-left: 12px;
    padding-right: 12px;
}

.input-group:focus-within {
    border-color: #3b82f6 !important;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15) !important;
}

.fam-th-sortable {
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
}

.fam-th-sortable:hover {
    color: #3b82f6;
}

.fam-filter-select {
    height: 38px !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 8px !important;
    font-size: 13px !important;
    color: #1e293b !important;
    background: #fff !important;
    min-width: 170px;
}

.fam-per-page-select {
    height: 30px !important;
    width: 60px !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    font-size: 12px !important;
    color: #475569 !important;
    background: #fff !important;
    padding: 0 4px !important;
}

.fam-sn-toggle {
    background: none;
    border: none;
    padding: 2px 0;
    font-size: 11px;
    color: #64748b;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 0.15s ease;
}

.fam-sn-toggle:hover {
    color: #3b82f6;
}

.fam-sn-list {
    margin-top: 4px;
    padding: 6px 10px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    max-height: 120px;
    overflow-y: auto;
}

.fam-sn-item {
    font-size: 11px;
    color: #475569;
    font-family: 'Courier New', monospace;
    padding: 2px 0;
}
</style>
